/* ログインID変更 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Block from '../../molecules/Block';
import Text from '../../molecules/Text';
import axios from '../../axios';
import { handleBrowserBack } from '../../BrowserBack';
import LoadingModal from '../../molecules/LoadingModal';
import CenterButton from '../../atoms/CenterButton';
import TransitionButton from '../../molecules/TransitionButton';
import Popup from '../../molecules/Popup';

const LoginIdChange: React.FC = () => {
  // ログインID
  const [login_id, setLoginId] = useState('');
  // ログインID(確認)
  const [login_id_confirmation, setLoginIdConfirmation] = useState('');
  // ワンタイムパスワード
  const [onetime_password, setOneTimePassword] = useState('');
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);
  // ワンタイムパスワード入力欄
  const [show_one_time_password, setShowOneTimePassword] = useState(false);
  // メールアドレス変更不可フラグ
  const unchangeable_mail = document.getElementById('common-login-id-change')?.dataset
    .unchangeable_mail
    ? true
    : false;
  // エラーメッセージ
  const [error_login_id, setErrorLoginId] = useState('');
  const [error_login_id_confirmation, setErrorLoginIdConfirmation] = useState('');
  const [error_onetime_password, setErrorOnetimePassword] = useState('');

  // ダイアログの表示フラグ
  const [open_dialog_confirm, setOpenDialogConfirm] = useState(false);
  const [open_dialog_complete, setOpenDialogComplete] = useState(false);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
  }, []);

  //  「ワンタイムパスワードを送信する」を押下した時の処理
  const handleSendOneTimePassword = async () => {
    try {
      //ローディング画面表示
      setIsLoading(true);

      // エラーメッセージをリセット
      setErrorLoginId('');
      setErrorLoginIdConfirmation('');

      // POSTする情報のまとめ
      const login_id_info = {
        login_id: login_id,
        login_id_confirmation: login_id_confirmation,
      };

      await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/login_id/send_onetime_passsword`, login_id_info);

      setShowOneTimePassword(true);
    } catch (error) {
      // 失敗
      if (typeof error.response.data.message === 'object') {
        // バリデーションエラー表示
        setErrorMessage(error.response.data.message);
      } else {
        alert('システムエラーが発生しました');
      }
    } finally {
      // ローディング画面非表示
      setIsLoading(false);
    }
  };

  // 「ログインIDを変更する」を押下した時の処理
  const handleDoChangeLoginId = async () => {
    try {
      setIsLoading(true);
      setErrorOnetimePassword('');

      await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/login_id/validate_onetime_password`, {
        onetime_password: onetime_password,
      });

      setOpenDialogConfirm(true);
    } catch (error) {
      if (typeof error.response.data.message === 'object') {
        // バリデーションエラー表示
        setErrorMessage(error.response.data.message);
      } else {
        alert('システムエラーが発生しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ログインID変更確認ポップアップの「変更する」ボタンのクリックイベントを定義
  const handleLoginIdChange = async () => {
    try {
      //ローディング画面表示
      setIsLoading(true);

      // 変更確認ポップアップを閉じる
      setOpenDialogConfirm(false);

      await axios.put('login_id/confirm');

      // ログインID変更完了ポップアップを表示する
      setOpenDialogComplete(true);
    } catch (error) {
      // 失敗
      alert('システムエラーが発生しました');
    } finally {
      // ローディング画面非表示
      setIsLoading(false);
    }
  };

  // バリデーションエラー表示
  const setErrorMessage = (message: { [key: string]: string }) => {
    if (message.login_id) {
      // 新ログインIDエラー
      setErrorLoginId(message.login_id);
    }

    if (message.login_id_confirmation) {
      // 新ログインID(確認用)エラー
      setErrorLoginIdConfirmation(message.login_id_confirmation);
    }

    // ワンタイムパスワードエラー
    if (message.onetime_password) {
      setErrorOnetimePassword(message.onetime_password);
    }
  };

  // ログインID変更確認ポップアップ「戻る」押下時
  const handleDialogConfirmCancel = () => {
    setOpenDialogConfirm(false);
  };

  // 新ログインID入力イベント
  const handleChangeLoginId = (value: string) => {
    setLoginId(value);
  };

  // 新ログインID(確認用)入力イベント
  const handleChangeLoginIdConfirmation = (value: string) => {
    setLoginIdConfirmation(value);
  };

  // ワンタイムパスワード入力イベント
  const handleChangeOnetimePassword = (value: string) => {
    setOneTimePassword(value);
  };

  // マイページへ戻る
  const handleClickReturnMypage = () => {
    setOpenDialogComplete(false);
    setIsLoading(true);
    location.href = `${location.origin}${process.env.MIX_MOCK_URL}/mypage/list`;
  };

  return (
    <div className="mt-5">
      <Block
        label="ログインID変更"
        content={
          <>
            {unchangeable_mail && (
              <strong className="p-lead is-invalid">
                ※現在、借入申込中のためログインIDの変更はできません。
              </strong>
            )}
            <p className="p-lead my-4">
              ご登録のメールアドレス(ログインID)を変更します。
              <br />
              本サービスのログインIDはお知らせを通知するメール送信先として利用されます。
              <br />
              ログインID変更後は新ログインIDがお知らせを通知するメール送信先になります。
            </p>
            <div className="mt-3">
              <Text
                id="email"
                label="新ログインID"
                value={login_id}
                placeholder="example@example.com"
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeLoginId(event.target.value)
                }
                readonly={unchangeable_mail}
                disabled={false}
                maxlength={50}
                message={error_login_id}
                help_msg="※新ログインIDとして用いるメールアドレスを入力して下さい"
                display_border={true}
              />
              <Text
                id="email-confirm"
                label="新ログインID(確認用)"
                value={login_id_confirmation}
                placeholder="example@example.com"
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeLoginIdConfirmation(event.target.value)
                }
                readonly={unchangeable_mail}
                disabled={false}
                maxlength={50}
                message={error_login_id_confirmation}
                display_border={true}
              />
            </div>
            <p className="p-lead my-4">
              「ワンタイムパスワードを送信する」ボタンを押すと入力されたメールアドレスにログインID変更に用いるワンタイムパスワードを送信します。
            </p>
            <CenterButton
              btn_class="arrow-right my-5 btn-space"
              label="ワンタイムパスワードを送信する"
              is_disabled={unchangeable_mail}
              handleClick={handleSendOneTimePassword}
            />
            {show_one_time_password && (
              <div>
                <p className="p-lead my-4">
                  メールに記載されたワンタイムパスワードをワンタイムパスワード入力欄に入力して、「ログインIDを変更する」ボタンを押して下さい。
                </p>
                <div className="mt-3">
                  <Text
                    id="onetime_password"
                    label="ワンタイムパスワード"
                    value={onetime_password}
                    placeholder=""
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeOnetimePassword(event.target.value)
                    }
                    disabled={false}
                    message={error_onetime_password}
                    display_border={true}
                  />
                </div>
                <CenterButton
                  btn_class="arrow-right my-5"
                  label="ログインIDを変更する"
                  is_disabled={false}
                  handleClick={handleDoChangeLoginId}
                />
              </div>
            )}
            <TransitionButton
              label="マイページへ戻る"
              btn_class="two_lines"
              handleClick={handleClickReturnMypage}
            />
            <Popup
              title="ログインID変更確認"
              text="ご登録のメールアドレス(ログインID)の変更を行います。よろしいですか？"
              first_button={{
                label: '変更する',
                variant: 'success',
                handleClick: handleLoginIdChange,
              }}
              second_button={{
                label: '戻る',
                variant: 'outline-success',
                handleClick: handleDialogConfirmCancel,
              }}
              toggleClose={handleDialogConfirmCancel}
              toggleShow={open_dialog_confirm}
            />
            <Popup
              title="ログインID変更完了"
              text="ご登録のメールアドレス(ログインID)の変更が完了しました。"
              first_button={{
                label: '閉じる',
                variant: 'outline-success',
                handleClick: handleClickReturnMypage,
              }}
              toggleClose={handleClickReturnMypage}
              toggleShow={open_dialog_complete}
            />
          </>
        }
      />
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </div>
  );
};
if (document.getElementById('common-login-id-change')) {
  ReactDOM.render(<LoginIdChange />, document.getElementById('common-login-id-change'));
}
